<template>
  <v-card id="ai-chat-card" class="pa-4">
    <div class="d-flex align-center">
      <div class="felix-avatar-container mr-2">
        <v-img
          src="../../assets/felix/icon/Icone_SemFundo.svg"
          max-width="40"
          height="38"
          contain
          class="felix-avatar"
        ></v-img>
      </div>
      <strong>
        Felix
        <v-chip small color="primary" class="ml-2">BETA</v-chip>
      </strong>

      <div class="ml-3 d-flex flex-column align-start">
        <span class="limit-text">
          Limite:
          {{
            aiUsage?.messageCount !== undefined && aiUsage?.limite !== undefined
              ? aiUsage.messageCount + '/' + aiUsage.limite
              : '-'
          }}
        </span>
      </div>
      <v-spacer></v-spacer>

      <v-btn icon @click="startNewSession" text>
        <v-icon>mdi-plus</v-icon>
      </v-btn>

      <v-menu
        v-if="$vuetify.breakpoint.smAndUp"
        v-model="expandMenu"
        offset-y
        left
      >
        <template #activator="{ on, attrs }">
          <v-btn icon class="mx-2" v-bind="attrs" v-on="on" text>
            <v-icon>mdi-dock-window</v-icon>
          </v-btn>
        </template>
        <div class="d-flex align-center pa-4" style="background: #ffffff">
          <v-list-item
            link
            class="d-block text-center pa-2 expand-item"
            @click="toLeft"
          >
            <v-icon> mdi-format-horizontal-align-left </v-icon>
            <br />
            Esquerda
          </v-list-item>

          <v-list-item
            link
            class="d-block text-center pa-2 expand-item"
            @click="toRight"
          >
            <v-icon>mdi-format-horizontal-align-right</v-icon>
            <br />
            Direita
          </v-list-item>
        </div>
      </v-menu>

      <v-btn
        x-small
        fab
        elevation="0"
        color="secondary"
        @click="$emit('close')"
      >
        <v-icon small>mdi-close</v-icon>
      </v-btn>
    </div>

    <div class="tabs-container mt-3 mb-3">
      <v-tabs
        v-model="activeTab"
        background-color="transparent"
        color="primary"
        grow
        @change="onTabChange"
      >
        <v-tabs-slider color="#BBCF20"></v-tabs-slider>
        <v-tab
          active-class="activated-tab"
          class="rounded-lg rounded-b-0 tab-button"
          key="sistema"
        >
          <v-icon left small>mdi-message-text</v-icon>
          Sistema
        </v-tab>

        <v-tab
          key="manuais"
          active-class="activated-tab"
          class="rounded-lg rounded-b-0 tab-button"
        >
          <v-icon left small>mdi-book-open-page-variant</v-icon>
          Manuais
        </v-tab>

        <v-tab
          key="pops"
          active-class="activated-tab"
          class="rounded-lg rounded-b-0 tab-button"
        >
          <v-icon left small>mdi-file-document</v-icon>
          POP's
        </v-tab>
      </v-tabs>
    </div>

    <v-divider class="ma-4 mt-1"></v-divider>

    <div id="ai-messages" ref="messagesContainer">
      <v-card
        v-if="currentMessages.length == 0 && !isProcessing"
        flat
        class="d-flex justify-center align-center ma-2 pa-4 primary white--text"
        style="min-height: 200px"
      >
        <div class="text-center">
          <div class="d-flex justify-center felix-intro-container">
            <video
              :src="felixVideos.presentation"
              width="300"
              height="300"
              autoplay
              loop
              muted
              playsinline
              class="felix-intro-video"
              @error="showIntroVideo = false"
              @loadeddata="onIntroVideoLoaded"
            ></video>
          </div>
          <strong>{{ getTabWelcomeTitle() }}</strong>
          <br />
          <span class="caption">{{ getTabWelcomeMessage() }}</span>
        </div>
      </v-card>

      <div v-else>
        <div v-for="message in currentMessages" :key="message.id">
          <AIUserMessage v-if="message.role === 'User'" :message="message" />
          <AIAssistantMessage
            v-else-if="message.role === 'Assistant'"
            :message="message"
            :felixVideos="felixVideos"
            :felixStateKey="felixStateKeyForMessages"
          />
        </div>

        <div v-if="isProcessing" class="d-flex justify-start ma-2">
          <div class="d-flex align-center">
            <div class="felix-thinking-container mr-3">
              <video
                :src="felixVideos.searching"
                width="60"
                height="60"
                autoplay
                loop
                muted
                playsinline
                class="felix-thinking-video"
                @error="showThinkingVideo = false"
                @loadeddata="onThinkingVideoLoaded"
              ></video>
            </div>
            <v-card
              class="pa-3"
              color="primary"
              dark
              style="border-radius: 20px 20px 20px 5px; max-width: 75%"
            >
              <div class="d-flex align-center">
                <span>Pensando...</span>
                <v-progress-circular
                  indeterminate
                  size="16"
                  width="2"
                  class="ml-2"
                ></v-progress-circular>
              </div>
            </v-card>
          </div>
        </div>
      </div>
    </div>

    <v-form ref="form" @submit.prevent="sendMessage">
      <div v-if="aiUsage.canUseAi === false" class="limit-warning mb-2">
        <v-alert
          class="limit-warning-alert"
          type="error"
          dense
          outlined
          border="left"
          color="red"
          icon="mdi-alert-circle"
        >
          <span>Você atingiu o limite de mensagens da sua instituição.</span>
        </v-alert>
      </div>
      <v-textarea
        v-model="currentMessage"
        :rules="[(v) => !!v || 'Digite uma mensagem']"
        :placeholder="getTabPlaceholder()"
        background-color="white"
        outlined
        no-resize
        style="box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.25); border-radius: 5px"
        hide-details
        rows="3"
        :disabled="isProcessing || aiUsage.canUseAi === false"
        @keyup.enter.exact.prevent="sendMessage"
        @input="detectUserTyping"
      >
        <template #append>
          <v-btn
            elevation="0"
            fab
            color="primary"
            class="ml-4"
            x-small
            :loading="isProcessing"
            :disabled="
              (currentMessage && !currentMessage.trim()) ||
              isProcessing ||
              aiUsage.canUseAi === false
            "
            @click="sendMessage"
          >
            <v-icon x-small color="#F9F9F9">mdi-send-outline</v-icon>
          </v-btn>
        </template>
      </v-textarea>
    </v-form>

    <div v-if="currentChatId" class="mt-2 text-caption text-center grey--text">
      Chat ID: {{ currentChatId }} ({{ getCurrentTabType() }})
      <span v-if="lastProcessingTime">
        | Último processamento: {{ lastProcessingTime }}ms</span
      >
    </div>

    <div
      v-if="currentChatId"
      class="mt-2 text-caption text-center warning--text"
    >
      Felix sempre responderá com base na instituição logada e pode cometer
      erros, pois é um modelo de IA em desenvolvimento.
    </div>
  </v-card>
</template>

<style scoped>
#ai-chat-card {
  position: fixed;
  z-index: 10 !important;
  margin: 10px;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.25) !important;
  border-radius: 6.25px !important;
  min-width: 405px;
  max-width: 505px;
  max-height: 90vh;
  overflow-y: auto;
  bottom: 0;
  right: 0;
}

#ai-messages {
  overflow-y: auto;
  height: unset;
  min-height: 54vh;
  max-height: 57vh;
  scroll-snap-type: y proximity;
}

#ai-messages::-webkit-scrollbar {
  width: 8px;
  border-radius: 6px;
}

#ai-messages::-webkit-scrollbar-thumb {
  width: 8px;
  border-radius: 6px;
  background-color: #ccc;
}

.expand-item:hover {
  background-color: #f5f5f5;
}

.tabs-container {
  width: 100%;
}

.tab-button {
  text-transform: none !important;
  font-weight: 500;
}

.felix-avatar-container {
  position: relative;
}

.felix-avatar {
  transition: all 0.3s ease-in-out;
}

.felix-avatar-video {
  width: 40px;
  height: 38px;
  object-fit: contain;
}

.felix-intro-video {
  max-width: 400px;
  height: 200px;
  object-fit: contain;
}

.felix-thinking-video {
  width: 60px;
  height: 60px;
  object-fit: contain;
}

.felix-intro-avatar {
  transition: transform 0.3s ease-in-out;
}

.felix-intro-container:hover .felix-intro-avatar {
  transform: scale(1.05) rotate(2deg);
}

.felix-intro-container:hover .felix-intro-video {
  transform: scale(1.05);
}

.limit-text {
  font-size: 14px;
  font-weight: 500;
}

.felix-thinking-container {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>

<script>
import anime from 'animejs'
import AIUserMessage from './AIUserMessage.vue'
import AIAssistantMessage from './AIAssistantMessage.vue'
import AIChatService from '../../services/AIChatService'
import FelixNeutral from '../../assets/felix/body1/Neutro_Corpo_1.png'
import FelixThinking from '../../assets/felix/body1/OlhandoPraBaixo_Corpo_1.png'
import FelixRelieved from '../../assets/felix/body1/Aliviado_Corpo_1.png'
import FelixScared from '../../assets/felix/body1/Assustado_Corpo_1.png'
import FelixDisappointed from '../../assets/felix/body1/Decepcionado_Corpo_1.png'
import FelixHappy from '../../assets/felix/body1/Satisfeito_Corpo_1.png'
import FelixLaughing from '../../assets/felix/body1/Gargalhada_Corpo_1.png'
import FelixAngry from '../../assets/felix/body1/Raiva_Corpo_1.png'
import FelixSad from '../../assets/felix/body1/Triste_Corpo_1.png'
import ApiService from '../../services/ApiService'

export default {
  name: 'AIChat',

  components: {
    AIUserMessage,
    AIAssistantMessage,
  },

  data() {
    return {
      isIdle: false,
      idleTimeout: null,
      idleMs: 30000,
      typingTimeout: null,
      expandMenu: false,
      showSessions: false,
      currentMessage: '',
      activeTab: 0,
      sessions: {
        sistema: {
          messages: [],
          chatId: 0,
        },
        manuais: {
          messages: [],
          chatId: 0,
        },
        pops: {
          messages: [],
          chatId: 0,
        },
      },
      apiService: new ApiService(),
      aiUsage: { limite: '-', messageCount: '-' },
      isProcessing: false,
      lastProcessingTime: null,
      chatService: new AIChatService(),
      felixVideos: {
        presentation: 'https://equipacarefix.blob.core.windows.net/public/00e1a52bb3be49f781d0f3da7f42d9f3.webm',
        neutral: 'https://equipacarefix.blob.core.windows.net/public/473b3417869d4f08bf832594c2982bdd.webm',
        searching: 'https://equipacarefix.blob.core.windows.net/public/7daee154de5141ab8c8385beee1b961b.webm',
        sleeping: 'https://equipacarefix.blob.core.windows.net/public/3d3faf6215d347ecab939cbeea34fa76.webm',
      },
      showHeaderVideo: true,
      showIntroVideo: true,
      showThinkingVideo: true,
      currentHeaderVideo: null,
      felixExpressions: {
        neutral: FelixNeutral,
        thinking: FelixThinking,
        relieved: FelixRelieved,
        scared: FelixScared,
        disappointed: FelixDisappointed,
        happy: FelixHappy,
        laughing: FelixLaughing,
        angry: FelixAngry,
        sad: FelixSad,
      },
      currentExpression: 'neutral',
      isTyping: false,
    }
  },

  computed: {
    currentFelixExpression() {
      return this.felixExpressions[this.currentExpression]
    },
    currentMessages() {
      const tabType = this.getCurrentTabType()
      return this.sessions[tabType].messages
    },
    currentChatId() {
      const tabType = this.getCurrentTabType()
      return this.sessions[tabType].chatId
    },
    currentFelixVideoKey() {
      const tabType = this.getCurrentTabType()
      
      if(this.aiUsage?.canUseAi === false) return 'sleeping'

      if(this.sessions[tabType].messages.length === 0 && !this.isProcessing){
        return 'presentation'
      }

      if(this.isProcessing || this.isTyping) return 'searching'

      if(this.isIdle) return 'sleeping'

      return 'neutral'
    },

    currentFelixVideo(){
      return this.felixVideos[this.currentFelixVideoKey]
    },

    felixStateKey() {
      if (this.aiUsage?.canUseAi === false) return 'sleeping'
      if (this.isProcessing || this.isTyping) return 'searching'
      if (this.isIdle) return 'sleeping'
      return 'neutral'
    },

    felixStateKeyForMessages() {
      const tabType = this.getCurrentTabType()


      if (this.sessions[tabType].messages.length === 0 && !this.isProcessing) {
        return 'presentation'
      }

      if (this.isProcessing || this.isTyping) {
        return 'searching'
      }

      if (this.isIdle) {
        return 'sleeping'
      }

      return 'neutral'
    },
  },

  async created() {
    this.userLogged = JSON.parse(localStorage.getItem('user'))
    this.institutionId = JSON.parse(localStorage.getItem('institution')).id
    this.institution = JSON.parse(localStorage.getItem('institution'))

    // Initialize header video
    this.currentHeaderVideo = this.felixVideos.neutral

    this.sessions.sistema.chatId = 0
    this.sessions.manuais.chatId = 0
    this.sessions.pops.chatId = 0

    this.updateInitialExpression()
    this.getAiUsage()
    this.resetIdleTimer()
  },

  watch: {
    currentMessages: {
      handler() {
        this.updateInitialExpression()
      },
      immediate: true,
    },
    activeTab() {
      this.scrollToBottom()
      this.updateInitialExpression()
    },
  },

  methods: {
    getCurrentTabType() {
      const tabMap = ['sistema', 'manuais', 'pops']
      return tabMap[this.activeTab]
    },

    getTabWelcomeTitle() {
      const titles = {
        sistema: 'Conheça o Felix!',
        manuais: 'Consulta de Manuais',
        pops: 'Procedimentos Operacionais Padrão',
      }
      return titles[this.getCurrentTabType()]
    },

    getTabWelcomeMessage() {
      const messages = {
        sistema:
          'O assistente inteligente do FixSystem. Aqui você pode fazer perguntas sobre os dados da sua instituição, consultar ordens de serviço, equipamentos, entre outros.',
        manuais:
          'Consulte manuais de equipamentos médicos. Tire suas dúvidas sobre instalação, operação, manutenção e especificações técnicas.',
        pops: 'Acesse os Procedimentos Operacionais Padrão da sua instituição. Consulte orientações, processos técnicos e boas práticas.',
      }
      return messages[this.getCurrentTabType()]
    },

    getTabPlaceholder() {
      const placeholders = {
        sistema: 'Digite sua pergunta sobre o sistema...',
        manuais: 'Consulte informações dos manuais de equipamentos...',
        pops: 'Busque orientações nos procedimentos operacionais...',
      }
      return placeholders[this.getCurrentTabType()]
    },

    onTabChange() {
      this.changeFelixExpression('neutral', false)
      this.scrollToBottom()
    },

    getAiUsage() {
      const userLogged = JSON.parse(localStorage.getItem('user') || '{}')
      const institution = JSON.parse(
        localStorage.getItem('institution') || '{}'
      )
      const institutionId = institution.id

      this.apiService
        .get(`ai/usage/${institutionId}`)
        .then((response) => {
          if (response.statusCode === 200) {
            const { limite, canUseAi, messageCount } = response.content
            this.aiUsage = { limite, canUseAi, messageCount }
          } else {
            this.$toast.error('Erro ao buscar uso da IA')
          }
        })
        .catch(() => {
          this.$toast.error('Erro na requisição de uso da IA')
        })
    },

    changeFelixExpression(expression, animate = true) {
      this.currentExpression = expression

      // Update header video based on state
      if (expression === 'thinking') {
        this.currentHeaderVideo = this.felixVideos.searching
      } else if (this.isProcessing) {
        this.currentHeaderVideo = this.felixVideos.searching
      } else {
        this.currentHeaderVideo = this.felixVideos.neutral
      }
    },

    onHeaderVideoLoaded(event) {
      event.target.play().catch(() => {
        this.showHeaderVideo = false
      })
    },

    onIntroVideoLoaded(event) {
      event.target.play().catch(() => {
        this.showIntroVideo = false
      })
    },

    onThinkingVideoLoaded(event) {
      event.target.play().catch(() => {
        this.showThinkingVideo = false
      })
    },

    detectUserTyping() {
      this.resetIdleTimer()

      this.isTyping = true

      if(this.typingTimeout) clearTimeout(this.typingTimeout)

      this.typingTimeout = setTimeout(() => {
        this.isTyping = false
        this.resetIdleTimer()
      }, 1200)
    },

    updateInitialExpression() {
      if (this.currentMessages.length === 0 && !this.isProcessing) {
        this.currentExpression = 'neutral'
      }
    },

    async sendMessage() {
      if (!this.currentMessage.trim() || this.isProcessing) return

      this.resetIdleTimer()

      const tabType = this.getCurrentTabType()
      const chatId = this.sessions[tabType].chatId

      const userMessage = {
        id: crypto?.randomUUID?.() || `${Date.now()}-u`,
        role: 'User',
        content: this.currentMessage,
        createdAt: new Date().toISOString(),
      }

      this.sessions[tabType].messages.push(userMessage)

      const messageToSend = this.currentMessage
      this.currentMessage = ''
      this.$refs.form.reset()

      this.scrollToBottom()

      // Start processing state
      this.isProcessing = true
      this.changeFelixExpression('thinking')

      const startTime = Date.now()

      try {
        const response = await this.apiService.post(
          'ai/send-message',
          {
            message: messageToSend,
            userId: this.userLogged.id,
            institutionId: this.institutionId,
            intentType: this.getIntentType(tabType),
            chatId: chatId,
          },
          false,
          false
        )

        if (response.statusCode === 200 && response.content) {
          // Update chatId if this was a new chat
          if (chatId === 0 && response.content.aiChatId) {
            this.sessions[tabType].chatId = response.content.aiChatId
          }

          const processingTime =
            response.content.processingTimeMs || (Date.now() - startTime)

          const assistantMessage = {
            id: crypto?.randomUUID?.() || `${Date.now()}-a`,
            role: 'Assistant',
            content: response.content.content,
            createdAt: response.content.createdAt,
            processingTimeMs: processingTime,
            tokensUsed: response.content.tokensUsed,
            cost: response.content.cost,
            felixVideoKey: this.getAssistantFelixVideoKey(
              response.content.content,
              processingTime
            ),
          }

          this.sessions[tabType].messages.push(assistantMessage)
          this.lastProcessingTime = assistantMessage.processingTimeMs

          this.changeFelixExpression('happy')
          setTimeout(() => {
            this.changeFelixExpression('neutral', false)
          }, 2000)

          this.scrollToBottom()
        } else {
          throw new Error(response.message || 'Erro ao enviar mensagem')
        }
      } catch (error) {
        this.$toast.error('Erro ao enviar mensagem para a IA')
        this.changeFelixExpression('scared')
        setTimeout(() => {
          this.changeFelixExpression('neutral', false)
        }, 3000)
      } finally {
        this.isProcessing = false
        this.resetIdleTimer()
        this.getAiUsage()
      }
    },

    getIntentType(tabType) {
      const intentMap = {
        sistema: 0,
        manuais: 1,
        pops: 2,
      }
      return intentMap[tabType] || 0
    },

    getInstitutionId() {
      const institution = JSON.parse(
        localStorage.getItem('institution') || '{}'
      )
      return institution.id || 1
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },

    toLeft() {
      anime({
        targets: '#ai-chat-card',
        translateX: -(window.innerWidth - 541 - 30),
        width: '541px',
        duration: 3000,
        easing: 'easeOutExpo',
      })
      this.expandMenu = false
    },

    toRight() {
      anime({
        targets: '#ai-chat-card',
        translateX: 0,
        width: '541px',
        duration: 3000,
        easing: 'easeOutExpo',
      })
      this.expandMenu = false
    },

    startNewSession() {
      const tabType = this.getCurrentTabType()
      this.sessions[tabType].chatId = 0
      this.sessions[tabType].messages = []
      this.currentMessage = ''
      this.showSessions = false
      this.changeFelixExpression('neutral')
      this.$toast.success('Nova conversa iniciada')
    },

    async loadSession(session) {
      try {
        const tabType = this.getCurrentTabType()
        this.sessions[tabType].chatId = session.chatId
        this.showSessions = false

        const response = await this.chatService.getChatHistory(session.chatId)

        if (response.statusCode === 200 && response.content.data) {

          this.sessions[tabType].messages = response.content.data.map((m) => {
            const id =
              m.id ||
              crypto?.randomUUID?.() ||
              `${Date.now()}-${Math.random().toString(16).slice(2)}`

            if (m.role === 'Assistant') {
              return {
                ...m,
                id,
                felixVideoKey:
                  m.felixVideoKey ||
                  this.getAssistantFelixVideoKey(m.content, m.processingTimeMs),
              }
            }

            return { ...m, id }
          })

          this.scrollToBottom()
        }

        this.$toast.success('Conversa carregada')
      } catch (error) {
        this.$toast.error('Erro ao carregar conversa')
      }
    },

    resetIdleTimer(){
      this.isIdle = false

      if(this.idleTimeout) clearTimeout(this.idleTimeout)

      this.idleTimeout = setTimeout(() => {
        if(!this.isProcessing && !this.isTyping){
          this.isIdle = true
        } 
      }, this.idleMs)
    },

    getAssistantFelixVideoKey(content, processingTimeMs) {
      const text = (content || '').toLowerCase()
      const ms = Number(processingTimeMs) || 0

      if (text.includes('erro') || text.includes('desculpe') || text.includes('não consegui')) {
        return 'sleeping'
      }

      if (text.includes('sucesso') || text.includes('encontrei') || text.includes('aqui está')) {
        return 'neutral'
      }

      if (text.includes('não encontrei') || text.includes('sem resultados')) {
        return 'sleeping'
      }

      if (ms >= 1200) return 'searching'
      return 'neutral'
    },
  },
}
</script>
